import React, { useState } from "react";
import { motion } from "framer-motion";
import AudioPlayer from "./AudioPlayer";
import StarVote from "./StarVote";
import ShareButton from "./ShareButton";
import VideoModal from "./VideoModal";
import { SubscribeBox, CommentsBox } from "./SlotEngagement";
import { fileUrl } from "../lib/api";
import { InstagramLogo, TiktokLogo, YoutubeLogo, SpotifyLogo, SoundcloudLogo, XLogo, Link as LinkIcon, AppleLogo, MusicNote, PlayCircle } from "@phosphor-icons/react";

const ICONS = {
  spotify: SpotifyLogo,
  apple: AppleLogo,
  applemusic: AppleLogo,
  soundcloud: SoundcloudLogo,
  instagram: InstagramLogo,
  tiktok: TiktokLogo,
  youtube: YoutubeLogo,
  twitter: XLogo,
  x: XLogo,
  website: LinkIcon,
};

function getIcon(platform) {
  const k = (platform || "").toLowerCase().replace(/\s+/g, "");
  return ICONS[k] || LinkIcon;
}

export default function SlotCard({ slot, index, shareSlogan, shareSloganByPlatform, shareHashtags, shareButtonColor }) {
  const reverse = index % 2 === 1;
  const [videoOpen, setVideoOpen] = useState(false);
  const [stats, setStats] = useState({
    vote_count: slot.vote_count || 0,
    avg_rating: slot.avg_rating || 0,
    display_count: slot.display_count || 0,
  });

  const handleVoted = (data) => {
    setStats({
      vote_count: data.vote_count,
      avg_rating: data.avg_rating,
      display_count: data.display_count,
    });
  };

  const hasVideo = Boolean(slot.video_url);

  return (
    <motion.article
      id={`slot-${slot.rank}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative group"
      data-testid={`slot-card-${slot.rank}`}
    >
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* Artwork */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-square w-full overflow-hidden rounded-md neon-border bg-ink-800">
            {slot.image_path ? (
              <img
                src={fileUrl(slot.image_path)}
                alt={`${slot.song_title} by ${slot.artist_name}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                data-testid={`slot-image-${slot.rank}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ink-800 to-ink-900">
                <MusicNote size={72} className="text-ink-600" weight="duotone" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 glass rounded-full">
              <span className="text-[10px] font-bold tracking-[0.25em] text-neon-cyan uppercase">Rank</span>
              <span className="font-mono text-xs text-white">#{String(slot.rank).padStart(2, "0")}</span>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] text-neon-cyan uppercase mb-3" data-testid={`slot-rank-label-${slot.rank}`}>
              No. {String(slot.rank).padStart(2, "0")} / Top Ten
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white text-balance leading-[0.95]" data-testid={`slot-title-${slot.rank}`}>
              {slot.song_title}
            </h2>
            <p className="mt-3 text-xl sm:text-2xl text-zinc-400 font-light tracking-tight" data-testid={`slot-artist-${slot.rank}`}>
              by <span className="text-white font-medium">{slot.artist_name}</span>
            </p>
          </div>

          <AudioPlayer src={slot.audio_path} rank={slot.rank} />

          <StarVote
            rank={slot.rank}
            voteCount={stats.vote_count}
            avgRating={stats.avg_rating}
            displayCount={stats.display_count}
            onVoted={handleVoted}
          />

          <div className="flex items-center gap-2 flex-wrap" data-testid={`slot-actions-${slot.rank}`}>
            <ShareButton rank={slot.rank} title={slot.song_title} artist={slot.artist_name} shareCode={slot.share_code} slogan={shareSlogan} sloganByPlatform={shareSloganByPlatform} hashtags={shareHashtags} buttonColor={shareButtonColor} />
            {hasVideo && (
              <button
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-ink-600 bg-ink-800/50 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 transition-all duration-300 text-xs font-medium"
                data-testid={`video-button-${slot.rank}`}
              >
                <PlayCircle size={14} weight="fill" /> Watch video
              </button>
            )}
          </div>

          {slot.bio && (
            <p className="text-base leading-relaxed text-zinc-400 max-w-2xl" data-testid={`slot-bio-${slot.rank}`}>
              {slot.bio}
            </p>
          )}

          {Array.isArray(slot.social_links) && slot.social_links.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid={`slot-socials-${slot.rank}`}>
              {slot.social_links.map((sl, i) => {
                if (!sl.url) return null;
                const Icon = getIcon(sl.platform);
                const linkKey = `${slot.rank}-${sl.platform}-${sl.url}`;
                return (
                  <a
                    key={linkKey}
                    href={sl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/social inline-flex items-center gap-2 px-3 py-2 rounded-md border border-ink-600 bg-ink-800/50 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 transition-all duration-300"
                    data-testid={`slot-social-${slot.rank}-${i}`}
                  >
                    <Icon size={16} weight="bold" />
                    <span className="text-xs font-medium tracking-wide">{sl.platform}</span>
                  </a>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <SubscribeBox rank={slot.rank} artistName={slot.artist_name} initialCount={slot.subscriber_count || 0} />
            <CommentsBox rank={slot.rank} />
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="mt-20 lg:mt-28 h-px w-full bg-gradient-to-r from-transparent via-ink-600 to-transparent" />

      <VideoModal
        open={videoOpen}
        url={slot.video_url}
        title={`${slot.song_title} — ${slot.artist_name}`}
        onClose={() => setVideoOpen(false)}
      />
    </motion.article>
  );
}
